/**
 * titleSearchStatus.ts
 *
 * Pure mapping from a Title Search (TR) job to the words shown in the Workspace:
 * the Status_Pill text, the item title and the Rail's step list. No React, no
 * I/O.
 *
 * Requirements: 3.2, 3.5, 3.6.
 */

import { JobStatus, type Job } from '../graphql/types';
import type { PillTone, StatusDotTone } from '../components/ui';
import { getStepStatus } from '../titleSearch/steps';
import { isTitleReviewFinalized } from './rowMode';
import type { WorkspaceMode } from './urlState';

/** The slice of a TR `Job` these helpers read. */
export type TitleSearchStatusJob = Pick<
  Job,
  | 'name'
  | 'status'
  | 'propertyAddress'
  | 'documentProcessingStatus'
  | 'findingsDisplayStatus'
  | 'titleAnalysisStatus'
>;

/**
 * True once the reader has opened Title analysis from Findings, which is what
 * completes the Findings step.
 */
export function hasOpenedTitleAnalysis(
  job: Pick<Job, 'findingsDisplayStatus' | 'titleAnalysisStatus'>
): boolean {
  const findings = getStepStatus(job, 'findings_display');
  if (findings === 'completed' || findings === 'not_found') return true;
  // An analysis that has run (or is running) means Title analysis was opened,
  // including searches that ran it before Findings was recorded as completed.
  const analysis = norm(job.titleAnalysisStatus);
  return analysis === 'in_progress' || analysis === 'completed' || analysis === 'failed';
}

export interface TitleSearchStatusPill {
  label: string;
  tone: PillTone;
}

function norm(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

/**
 * Item title for the Top_Bar: the property address when present (what a title
 * reviewer recognises a search by), otherwise the search name.
 */
export function getTitleSearchTitle(
  job: Pick<Job, 'name' | 'propertyAddress'> | null | undefined,
  fallback: string
): string {
  const address = job?.propertyAddress?.trim();
  if (address) return address;
  const name = job?.name?.trim();
  return name || fallback;
}

/**
 * Stage in words for the Status_Pill: "Intake", "Processing documents",
 * "Data review", "Findings", "Title analysis" (with "running" / "failed" where
 * that is the state), or a terminal state.
 */
export function getTitleSearchStatusPill(job: TitleSearchStatusJob): TitleSearchStatusPill {
  if (job.status === JobStatus.ARCHIVED) return { label: 'Archived', tone: 'neutral' };
  if (job.status === JobStatus.FAILED) return { label: 'Failed', tone: 'danger' };

  if (isTitleReviewFinalized(job)) {
    switch (norm(job.titleAnalysisStatus)) {
      case 'in_progress':
        return { label: 'Title analysis running', tone: 'selected' };
      case 'completed':
        return { label: 'Title analysis', tone: 'success' };
      case 'failed':
        return { label: 'Title analysis failed', tone: 'danger' };
      default:
        return hasOpenedTitleAnalysis(job)
          ? { label: 'Title analysis', tone: 'selected' }
          : { label: 'Findings', tone: 'selected' };
    }
  }

  switch (getStepStatus(job, 'document_processing')) {
    case 'in_progress':
      return { label: 'Processing documents', tone: 'selected' };
    case 'failed':
    case 'blocked':
      return { label: 'Processing failed', tone: 'danger' };
    default:
      return job.status === JobStatus.DOCUMENT_VERIFICATION
        ? { label: 'Data review', tone: 'warning' }
        : { label: 'Intake', tone: 'neutral' };
  }
}

export type TitleSearchRailStepKey = 'document_processing' | 'findings' | 'title_analysis';

export interface TitleSearchRailStep {
  key: TitleSearchRailStepKey;
  label: string;
  /** Status in words, shown beside the dot. */
  statusLabel: string;
  tone: StatusDotTone;
  /** The Mode this step opens. */
  mode: WorkspaceMode;
}

/**
 * Steps for the Rail's StepList, each with a dot tone, a status in words and
 * the Mode it opens: Document processing → Review, Findings → Findings,
 * Title analysis → Title analysis.
 */
export function getTitleSearchRailSteps(job: TitleSearchStatusJob): TitleSearchRailStep[] {
  const finalized = isTitleReviewFinalized(job);

  const doc = getStepStatus(job, 'document_processing');
  const docStep: TitleSearchRailStep = {
    key: 'document_processing',
    label: 'Document processing',
    mode: 'review',
    ...(doc === 'completed' || doc === 'not_found'
      ? { tone: 'success' as const, statusLabel: 'Completed' }
      : doc === 'in_progress'
        ? { tone: 'running' as const, statusLabel: 'In progress' }
        : doc === 'failed' || doc === 'blocked'
          ? { tone: 'danger' as const, statusLabel: 'Failed' }
          : { tone: 'idle' as const, statusLabel: 'Not started' }),
  };

  // Findings is in progress once Data Review is finalized, and completed once
  // the reader opens Title analysis. Searches finalized before the backend set
  // `in_progress` still read "pending", so treat that as in progress too.
  const storedFindings = getStepStatus(job, 'findings_display');
  const findings = !finalized
    ? 'pending'
    : hasOpenedTitleAnalysis(job)
      ? 'completed'
      : storedFindings === 'pending'
        ? 'in_progress'
        : storedFindings;
  const findingsStep: TitleSearchRailStep = {
    key: 'findings',
    label: 'Findings',
    mode: 'findings',
    ...(findings === 'completed' || findings === 'not_found'
      ? { tone: 'success' as const, statusLabel: 'Completed' }
      : findings === 'in_progress'
        ? { tone: 'running' as const, statusLabel: 'In progress' }
        : findings === 'failed' || findings === 'blocked'
          ? { tone: 'danger' as const, statusLabel: 'Failed' }
          : { tone: 'idle' as const, statusLabel: 'Not started' }),
  };

  // Title analysis is in progress from the moment the reader opens it (which
  // completes Findings) until an analysis has completed.
  const analysis = finalized ? norm(job.titleAnalysisStatus) : '';
  const analysisOpened = finalized && hasOpenedTitleAnalysis(job);
  const analysisStep: TitleSearchRailStep = {
    key: 'title_analysis',
    label: 'Title analysis',
    mode: 'analysis',
    ...(analysis === 'completed'
      ? { tone: 'success' as const, statusLabel: 'Completed' }
      : analysis === 'in_progress'
        ? { tone: 'running' as const, statusLabel: 'In progress' }
        : analysis === 'failed' || analysis === 'blocked'
          ? { tone: 'danger' as const, statusLabel: 'Failed' }
          : analysisOpened
            ? { tone: 'running' as const, statusLabel: 'In progress' }
            : { tone: 'idle' as const, statusLabel: 'Not run' }),
  };

  return [docStep, findingsStep, analysisStep];
}
